/**
 * Recipes page — admin and user management of reusable multi-ingredient recipes.
 *
 * Layout mirrors MealTemplates.tsx:
 *  - Header card list with create / edit / delete actions
 *  - RecipeFormDialog: searchable ingredient editor, live nutrition preview,
 *    weight-override toggle, visibility toggle (public/private)
 */

import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  ChefHat,
  Globe,
  Lock,
  X,
  FlameKindling,
  Tag,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useAuth } from "@/_core/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DraftIngredient {
  /** undefined = not yet saved to DB (new item in dialog) */
  id?: number;
  potravinaId: number;
  nazov: string;
  hmotnostG: string;
  /** kcal per 100g of this food */
  energiaKcal100: string;
}

// ---------------------------------------------------------------------------
// Nutrition helpers
// ---------------------------------------------------------------------------

function computeRecipeNutrition(
  items: DraftIngredient[],
  overrideHmotnost: string
): {
  sumG: number;
  effectiveG: number;
  kcalTotal: number;
  kcalPer100g: number;
} {
  let sumG = 0;
  let kcalTotal = 0;
  for (const item of items) {
    const w = parseFloat(item.hmotnostG) || 0;
    sumG += w;
    kcalTotal += (parseFloat(item.energiaKcal100) || 0) * (w / 100);
  }
  const effectiveG = overrideHmotnost
    ? parseFloat(overrideHmotnost) || sumG
    : sumG;
  const kcalPer100g = effectiveG > 0 ? (kcalTotal / effectiveG) * 100 : 0;
  return { sumG, effectiveG, kcalTotal, kcalPer100g };
}

// ---------------------------------------------------------------------------
// RecipeFormDialog
// ---------------------------------------------------------------------------

interface RecipeFormDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editRecipe?: {
    id: number;
    nazov: string;
    jeVerejny: boolean;
    celkovaHmotnostG: string | null;
    poznamka: string | null;
    items: DraftIngredient[];
    tagIds?: number[];
  } | null;
  potraviny: Array<{
    id: number;
    nazov: string;
    energiaKcal: string | null;
    energiaKj: string | null;
  }>;
  allTags: { id: number; nazov: string; farba: string | null }[];
  canManageGlobalRecipes: boolean;
  canManageTags: boolean;
  onSaved: () => void;
}

export function RecipeFormDialog({
  open,
  onOpenChange,
  editRecipe,
  potraviny,
  allTags,
  canManageGlobalRecipes,
  canManageTags,
  onSaved,
}: RecipeFormDialogProps) {
  const { t } = useTranslation();
  const [nazov, setNazov] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [jeVerejny, setJeVerejny] = useState(false);
  const [overrideWeight, setOverrideWeight] = useState(false);
  const [overrideHmotnost, setOverrideHmotnost] = useState("");
  const [items, setItems] = useState<DraftIngredient[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [foodSearch, setFoodSearch] = useState("");
  const [foodOpen, setFoodOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (open) {
      if (editRecipe) {
        setNazov(editRecipe.nazov);
        setPoznamka(editRecipe.poznamka ?? "");
        setJeVerejny(editRecipe.jeVerejny);
        const hasOverride = !!editRecipe.celkovaHmotnostG;
        setOverrideWeight(hasOverride);
        setOverrideHmotnost(editRecipe.celkovaHmotnostG ?? "");
        setItems(editRecipe.items);
        setSelectedTagIds(editRecipe.tagIds ?? []);
      } else {
        setNazov("");
        setPoznamka("");
        setJeVerejny(false);
        setOverrideWeight(false);
        setOverrideHmotnost("");
        setItems([]);
        setSelectedTagIds([]);
      }
      setFoodSearch("");
    }
  }, [open, editRecipe]);

  const createMutation = trpc.recept.create.useMutation();
  const updateMutation = trpc.recept.update.useMutation();
  const addItemMutation = trpc.recept.addItem.useMutation();
  const updateItemMutation = trpc.recept.updateItem.useMutation();
  const removeItemMutation = trpc.recept.removeItem.useMutation();
  const setTagsMutation = trpc.tag.setForRecept.useMutation();

  const toggleTag = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const nutrition = useMemo(
    () => computeRecipeNutrition(items, overrideWeight ? overrideHmotnost : ""),
    [items, overrideWeight, overrideHmotnost]
  );

  const filteredFoods = useMemo(() => {
    const term = foodSearch.toLowerCase();
    return term
      ? potraviny.filter(p => p.nazov.toLowerCase().includes(term)).slice(0, 50)
      : potraviny.slice(0, 50);
  }, [potraviny, foodSearch]);

  const addIngredient = (food: (typeof potraviny)[0]) => {
    const kcal100 = food.energiaKcal
      ? food.energiaKcal
      : food.energiaKj
        ? (parseFloat(food.energiaKj) / 4.18).toFixed(2)
        : "0";
    setItems(prev => [
      ...prev,
      {
        potravinaId: food.id,
        nazov: food.nazov,
        hmotnostG: "100",
        energiaKcal100: kcal100,
      },
    ]);
    setFoodOpen(false);
    setFoodSearch("");
  };

  const removeIngredient = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateIngredientWeight = (idx: number, val: string) => {
    setItems(prev =>
      prev.map((item, i) => (i === idx ? { ...item, hmotnostG: val } : item))
    );
  };

  const handleSave = async () => {
    if (!nazov.trim()) {
      toast.error(t("mealLibrary:recept.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const celkovaHmotnostG =
        overrideWeight && overrideHmotnost ? overrideHmotnost : null;

      let savedId: number | undefined;
      if (editRecipe) {
        savedId = editRecipe.id;
        // Update header
        await updateMutation.mutateAsync({
          id: editRecipe.id,
          data: {
            nazov: nazov.trim(),
            jeVerejny,
            celkovaHmotnostG,
            poznamka: poznamka || null,
          },
        });

        // Reconcile ingredients:
        // Remove items that were in editRecipe but not in current items
        const currentIds = new Set(
          items.filter(i => i.id !== undefined).map(i => i.id!)
        );
        for (const orig of editRecipe.items) {
          if (orig.id !== undefined && !currentIds.has(orig.id)) {
            await removeItemMutation.mutateAsync({ id: orig.id });
          }
        }
        // Update existing items whose weight changed
        for (const item of items) {
          if (item.id !== undefined) {
            const orig = editRecipe.items.find(o => o.id === item.id);
            if (orig && orig.hmotnostG !== item.hmotnostG) {
              await updateItemMutation.mutateAsync({
                id: item.id,
                hmotnostG: item.hmotnostG,
              });
            }
          } else {
            // New item added during edit
            await addItemMutation.mutateAsync({
              receptId: editRecipe.id,
              potravinaId: item.potravinaId,
              hmotnostG: item.hmotnostG,
            });
          }
        }
      } else {
        // Create header
        const created = await createMutation.mutateAsync({
          nazov: nazov.trim(),
          klientId: canManageGlobalRecipes ? null : undefined,
          jeVerejny,
          celkovaHmotnostG,
          poznamka: poznamka || null,
        });
        savedId = created.id;
        // Add all ingredients
        for (const item of items) {
          await addItemMutation.mutateAsync({
            receptId: created.id,
            potravinaId: item.potravinaId,
            hmotnostG: item.hmotnostG,
          });
        }
      }

      // Tags are taxonomy-like metadata managed by admins only. Regular
      // users can still create/edit their own recipe content without calling
      // the admin-only tag mutation.
      if (savedId !== undefined && canManageTags) {
        await setTagsMutation.mutateAsync({
          receptId: savedId,
          tagIds: selectedTagIds,
        });
      }
      toast.success(
        editRecipe
          ? t("mealLibrary:recept.updated")
          : t("mealLibrary:recept.created")
      );
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(t("mealLibrary:recept.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {editRecipe
              ? t("mealLibrary:recept.editTitle")
              : t("mealLibrary:recept.createTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-1">
              <Label>{t("mealLibrary:recept.nameRequiredLabel")}</Label>
              <Input
                value={nazov}
                onChange={e => setNazov(e.target.value)}
                placeholder={t("mealLibrary:recept.namePlaceholder")}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label>{t("mealLibrary:recept.notes")}</Label>
              <Textarea
                value={poznamka}
                onChange={e => setPoznamka(e.target.value)}
                placeholder={t("mealLibrary:recept.notesPlaceholder")}
                className="min-h-[60px]"
              />
            </div>

            {/* Tags */}
            {canManageTags && allTags.length > 0 && (
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5" />
                  {t("mealLibrary:tags.label")}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {allTags.map(tag => {
                    const selected = selectedTagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary"
                        }`}
                        style={
                          selected && tag.farba
                            ? {
                                backgroundColor: tag.farba,
                                borderColor: tag.farba,
                                color: "#fff",
                              }
                            : tag.farba && !selected
                              ? { borderColor: tag.farba, color: tag.farba }
                              : undefined
                        }
                      >
                        {tag.nazov}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Visibility */}
            <div className="flex items-center gap-3">
              <Switch
                id="jeVerejny"
                checked={jeVerejny}
                onCheckedChange={setJeVerejny}
              />
              <Label htmlFor="jeVerejny" className="cursor-pointer">
                {jeVerejny ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <Globe className="h-4 w-4" />{" "}
                    {t("mealLibrary:recept.visibilityPublicHint")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Lock className="h-4 w-4" />{" "}
                    {t("mealLibrary:recept.private")}
                  </span>
                )}
              </Label>
            </div>

            {/* Weight override */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Switch
                  id="overrideWeight"
                  checked={overrideWeight}
                  onCheckedChange={setOverrideWeight}
                />
                <Label htmlFor="overrideWeight" className="cursor-pointer">
                  {t("mealLibrary:recept.overrideWeight")}
                </Label>
              </div>
              {overrideWeight && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={overrideHmotnost}
                    onChange={e => setOverrideHmotnost(e.target.value)}
                    placeholder={t("mealLibrary:recept.defaultWeight", {
                      weight: nutrition.sumG.toFixed(0),
                    })}
                    className="w-40"
                    min="1"
                    step="1"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("mealLibrary:recept.finishedWeightUnit")}
                  </span>
                </div>
              )}
            </div>

            {/* Nutrition summary */}
            {items.length > 0 && (
              <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
                <div className="font-medium flex items-center gap-1">
                  <FlameKindling className="h-4 w-4 text-orange-500" />
                  {t("mealLibrary:recept.nutritionSummary")}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="font-bold text-primary">
                      {nutrition.kcalTotal.toFixed(0)} kcal
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("mealLibrary:recept.total")}
                    </div>
                  </div>
                  <div>
                    <div className="font-bold">
                      {nutrition.effectiveG.toFixed(0)} g
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {overrideWeight
                        ? t("mealLibrary:recept.finishedWeight")
                        : t("mealLibrary:recept.sumOfIngredients")}
                    </div>
                    {/* When override is active, also show the raw ingredient sum */}
                    {overrideWeight &&
                      nutrition.sumG !== nutrition.effectiveG && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {t("mealLibrary:recept.ingredientsWeight", {
                            weight: nutrition.sumG.toFixed(0),
                          })}
                        </div>
                      )}
                  </div>
                  <div>
                    <div className="font-bold text-orange-600">
                      {nutrition.kcalPer100g.toFixed(1)} kcal/100g
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("mealLibrary:recept.per100g")}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ingredient list */}
            <div className="space-y-2">
              <Label>{t("mealLibrary:recept.ingredients")}</Label>
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t("mealLibrary:recept.noIngredients")}
                </p>
              )}
              {items.map((item, idx) => (
                <div
                  key={`${item.potravinaId}-${idx}`}
                  className="flex items-center gap-2 rounded-md border p-2"
                >
                  <span className="flex-1 text-sm truncate">{item.nazov}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {item.energiaKcal100
                      ? `${((parseFloat(item.energiaKcal100) * parseFloat(item.hmotnostG || "0")) / 100).toFixed(0)} kcal`
                      : ""}
                  </span>
                  <Input
                    type="number"
                    value={item.hmotnostG}
                    onChange={e => updateIngredientWeight(idx, e.target.value)}
                    className="w-20 h-7 text-sm"
                    min="0.1"
                    step="0.1"
                  />
                  <span className="text-xs text-muted-foreground">g</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => removeIngredient(idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Add ingredient */}
            <Popover open={foodOpen} onOpenChange={setFoodOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  <Plus className="h-4 w-4" />
                  {t("mealLibrary:recept.addIngredient")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[380px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t("mealLibrary:sablona.searchFood")}
                    value={foodSearch}
                    onValueChange={setFoodSearch}
                  />
                  <CommandList className="max-h-64 overflow-y-auto">
                    <CommandEmpty>
                      {t("mealLibrary:sablona.noFood")}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredFoods.map(food => (
                        <CommandItem
                          key={food.id}
                          value={food.nazov}
                          onSelect={() => addIngredient(food)}
                        >
                          <span className="flex-1 truncate">{food.nazov}</span>
                          {food.energiaKcal && (
                            <span className="text-xs text-muted-foreground ml-2">
                              {parseFloat(food.energiaKcal).toFixed(0)}{" "}
                              kcal/100g
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t bg-background">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? t("mealLibrary:sablona.saving")
              : editRecipe
                ? t("mealLibrary:recept.saveChanges")
                : t("mealLibrary:recept.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
